import React, { useState, useRef, useEffect } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-sql";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-language_tools";
import { Box, Button, Grid, IconButton, Tooltip } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import ResourceColumnsChips from "../../Organisms/ResourceColumnsChips";
import AvailableVariables from "../../Molecules/Variables/AvailableVariables";
import AvailableColumnsTabs from "./AvailableColumnsTabs";

interface SQLEditorProps {
  error?: any;
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  isQueryColumns?: any;
  availableVariableMarginGap?: number;
  mergeQuerycolumns?: any;
  isFinalQuery?: any;
  dialogType?: string;
  queryBuilderType?: string;
  mergeQueryType?: string;
  label?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  showAvailableVariables?: boolean; // New prop to control AvailableVariables display
  showResourceColumnsChips?: boolean; // New prop to control ResourceColumnsChips display
  // Hides the entire "Available Columns" section (and its "No columns available."
  // fallback) rendered below the editor, for consumers that show available
  // columns elsewhere (e.g. the Mixed V2 editor's side panel).
  hideAvailableColumns?: boolean;
  // Stable per-instance key used to persist editor UI state (expansion, cursor)
  // across remounts when navigating between sub-queries.
  editorStateKey?: string;
  // Custom state management props for multi-query builder
  customValue?: string;
  customOnChange?: (value: string) => void;
  customEditorRef?: any;
  customOnBlur?: () => void;
  // Optional: opens the standalone pop-out editor for this query. When provided,
  // a pop-out button is rendered in the editor toolbar next to the expand toggle.
  onPopOut?: () => void;
}

const SQLEditor: React.FC<SQLEditorProps> = ({
  error,
  setErrors,
  isQueryColumns,
  availableVariableMarginGap,
  mergeQuerycolumns,
  isFinalQuery,
  dialogType,
  queryBuilderType,
  mergeQueryType,
  label,
  onFocus,
  onBlur,
  showAvailableVariables = true, // Default to true for backward compatibility
  showResourceColumnsChips = true, // Default to true for backward compatibility
  hideAvailableColumns = false, // Default to false for backward compatibility
  editorStateKey,
  // Custom state management props
  customValue,
  customOnChange,
  customEditorRef,
  customOnBlur,
  onPopOut,
}) => {
  const {
    availColumns,
    availColumnsWithResourceDetail,
    queryBuilderTempValue,
    setQueryBuilderTempValue,
    editorRef,
    showAggregatedColumns,
    aggregatedAvailColumns,
    editorExpandedMap,
    setEditorExpanded,
    editorCursorMap,
    setEditorCursor,
  } = useRuleResourceContext();

  // Use custom state management if provided, otherwise fall back to context
  const currentValue =
    customValue !== undefined ? customValue : queryBuilderTempValue;
  const currentOnChange = customOnChange || setQueryBuilderTempValue;
  const currentEditorRef = customEditorRef || editorRef;

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  // Stable per-instance key used to persist editor UI state (expansion height and
  // cursor position) so it survives remounts when navigating between sub-queries.
  // Prefer an explicit editorStateKey (e.g. a sub-query id) when provided, since
  // it uniquely identifies same-typed sub-queries; otherwise fall back to a
  // composite of the editor's descriptive props.
  const stateKey =
    editorStateKey ||
    [dialogType, queryBuilderType, mergeQueryType, label]
      .filter(Boolean)
      .join("|") ||
    "sql-editor";
  const isExpanded = editorExpandedMap[stateKey] ?? false;
  const setIsExpanded = (expanded: boolean) =>
    setEditorExpanded(stateKey, expanded);

  // Tracks the latest cursor position without triggering re-renders on every
  // move. It is persisted to context on blur and on unmount, then restored when
  // the editor remounts for a previously-visited sub-query.
  const cursorPositionRef = useRef<{ row: number; column: number } | null>(
    null,
  );
  // Holds the Ace editor instance once it has loaded so the restore effect can
  // act on it reliably, independent of the shared editor ref timing.
  const loadedEditorRef = useRef<any>(null);
  const hasRestoredRef = useRef(false);

  const persistCursorPosition = () => {
    if (cursorPositionRef.current) {
      setEditorCursor(stateKey, cursorPositionRef.current);
    }
  };

  const handleCursorChange = () => {
    const editor = loadedEditorRef.current || currentEditorRef.current?.editor;
    if (!editor) return;
    const { row, column } = editor.getCursorPosition();
    cursorPositionRef.current = { row, column };
  };

  // Capture the editor instance when react-ace finishes mounting it.
  const handleEditorLoad = (editor: any) => {
    loadedEditorRef.current = editor;
  };

  const handleEditorBlur = () => {
    persistCursorPosition();
    const externalBlur = customOnBlur || onBlur;
    if (externalBlur) externalBlur();
  };

  // Restore the saved cursor position after mount. This is deferred to a
  // macrotask so it runs after react-ace has set the initial value and done its
  // own cursor reset, which would otherwise clobber an immediate (onLoad)
  // restore. It only runs when a position was previously saved for this key, so
  // it never steals focus the first time an editor is opened.
  useEffect(() => {
    if (hasRestoredRef.current) return;
    const saved = editorCursorMap[stateKey];
    if (!saved) return;
    const timer = setTimeout(() => {
      const editor =
        loadedEditorRef.current || currentEditorRef.current?.editor;
      if (!editor) return;
      hasRestoredRef.current = true;
      cursorPositionRef.current = saved;
      editor.moveCursorToPosition(saved);
      editor.clearSelection();
      editor.scrollToLine(saved.row, true, true, () => undefined);
      editor.focus();
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the final cursor position when the editor unmounts (e.g. switching
  // sub-query tabs) so it can be restored on return.
  useEffect(() => {
    return () => {
      persistCursorPosition();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Line counts that drive the AceEditor height in each mode.
  const COLLAPSED_LINES = 10;
  const EXPANDED_LINES = 30;
  const editorLines = isExpanded ? EXPANDED_LINES : COLLAPSED_LINES;

  const handleSqlChange = (newSqlQuery: string) => {
    currentOnChange(newSqlQuery);
    if (newSqlQuery) {
      setErrors((prevError) => ({
        ...prevError,
        query: "",
      }));
    } else {
      setErrors((prevError) => ({
        ...prevError,
        query: "Please enter query",
      }));
    }
  };

  const handleColumnSelect = (column: string) => {
    const editor = currentEditorRef.current?.editor;
    if (!editor) return;

    const currentPosition = editor.getCursorPosition();
    const currentColumn = currentPosition.column;
    // Insert the column at the current cursor position
    editor.session.insert(currentPosition, `"${column}"`);
    // Calculate the new cursor position (column name + quotes + space)
    const newCursorPosition = {
      row: currentPosition.row,
      column: currentColumn + column.length + 3, // "column" + space = 3 extra chars
    };
    // Update cursor position and notify parent of the change
    const updatedValue = editor.session.getValue();
    currentOnChange(updatedValue);
    editor.moveCursorToPosition(newCursorPosition);
    editor.clearSelection();
    editor.focus();
  };

  const handleVariableSelect = (variable: string) => {
    const editor = currentEditorRef.current?.editor;
    if (!editor) return;

    const currentPosition = editor.getCursorPosition();
    const currentColumn = currentPosition.column;

    // FIX: Store the inserted string with trailing space
    // Following the pattern from ResourceColumnsChips.tsx (lines 145-153, 167)
    const insertedText = `$$${variable}$$ `;

    editor.session.insert(currentPosition, insertedText);

    // FIX: Calculate cursor position based on the full length of inserted text
    // This ensures cursor is positioned AFTER the trailing space
    const newCursorPosition = {
      row: currentPosition.row,
      column: currentColumn + insertedText.length,
    };

    editor.moveCursorToPosition(newCursorPosition);
    editor.clearSelection();
    editor.focus();
  };

  const handleColumnSelectWithPrefix = (
    column: string,
    resourceCode: string,
  ) => {
    const editor = currentEditorRef.current?.editor;
    if (!editor) return;

    const currentPosition = editor.getCursorPosition();
    const currentLine = editor.session.getLine(currentPosition.row);
    const currentColumn = currentPosition.column;
    // FIX: Store the inserted text in a variable
    // Following the pattern from handleVariableSelect (lines 110, 117)
    const insertedText = `"${resourceCode}.${column}" `;
    const updatedLine =
      currentLine.slice(0, currentColumn) +
      column +
      " " +
      currentLine.slice(currentColumn);

    currentOnChange(updatedLine);
    setSelectedColumns([...selectedColumns, insertedText]);
    editor.session.insert(currentPosition, insertedText);

    // FIX: Calculate cursor position based on the full length of inserted text
    // This ensures cursor is positioned AFTER the complete inserted string
    // Previously: column.length + 1 (only accounted for column name + 1 char)
    // Now: insertedText.length (accounts for opening quote + resourceCode + dot + column + closing quote)
    const newCursorPosition = {
      row: currentPosition.row,
      column: currentColumn + insertedText.length,
    };

    // Set the new cursor position
    editor.moveCursorToPosition(newCursorPosition);
    editor.clearSelection();
    editor.focus();
  };

  const editorOptions = {
    enableLiveAutocompletion: true,
    enableBasicAutocompletion: true,
    enableSnippets: false,
    showLineNumbers: true,
    tabSize: 2,
    fontSize: 20,
    wrap: true,
    // Allow the editor to grow fluidly with its content up to maxLines.
    maxLines: editorLines,
    minLines: editorLines,
    autoScrollEditorIntoView: true,
  };
  return (
    <>
      <Grid container columnSpacing={1.5} rowSpacing={2.5}>
        <Grid item xs={12} md={12}>
          <Box className="editor-header">
            <Box className="label-text">{label || "SQL query"}</Box>
            <Box className="editor-header-actions">
              {onPopOut && (
                <Tooltip title="Pop out query editor" placement="top">
                  <IconButton
                    size="small"
                    className="editor-popout-btn"
                    aria-label="pop out query editor"
                    onClick={onPopOut}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={isExpanded ? "Collapse editor" : "Expand editor"}
                placement="top"
              >
                <IconButton
                  size="small"
                  className="editor-expand-btn"
                  aria-label={isExpanded ? "collapse editor" : "expand editor"}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? (
                    <CloseFullscreenIcon fontSize="small" />
                  ) : (
                    <OpenInFullIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box className={`editor-parent ${isExpanded ? "expanded" : ""}`}>
            <AceEditor
              mode="sql"
              theme="github"
              onChange={handleSqlChange}
              onFocus={onFocus}
              onBlur={handleEditorBlur}
              onCursorChange={handleCursorChange}
              onLoad={handleEditorLoad}
              value={currentValue || ""}
              width="100%"
              editorProps={{ $blockScrolling: true }}
              setOptions={editorOptions}
              ref={currentEditorRef}
              showGutter
              aria-label="editor"
              name="editor"
              fontSize={16}
              minLines={editorLines}
              maxLines={editorLines}
              showPrintMargin={false}
              className={`sql-editor mb-0 mt-0 ${
                error?.query ? "has-error" : ""
              }`}
            />
          </Box>
          <span className="validation-error ml-0">
            {error?.query && <span>{error?.query}</span>}
          </span>
        </Grid>

        {showAvailableVariables && (
          <Grid item xs={12} md={isQueryColumns ? 12 : 6}>
            <Box
              sx={{
                marginBottom: availableVariableMarginGap
                  ? `${availableVariableMarginGap}px`
                  : "16px",
              }}
            >
              <Box className={`available-box `}>
                <AvailableVariables
                  error={error}
                  setErrors={setErrors}
                  handleVariableSelect={handleVariableSelect}
                />
              </Box>
            </Box>
          </Grid>
        )}
        {!hideAvailableColumns &&
          (showAggregatedColumns ? (
            // If `showAggregatedColumns` is true
            <Grid item xs={12} md={isQueryColumns ? 12 : 6}>
              <Box
                sx={{ marginBottom: 3 }}
                className="available-box box-alt avc"
              >
                <h3 className="mb-0 mb-8">Available Columns :</h3>
                <div className="avail-columns-group">
                  {aggregatedAvailColumns.map((column: any, index: any) => (
                    <button
                      className="avail-columns"
                      key={index}
                      onClick={() => handleColumnSelect(column)}
                    >
                      {column}
                    </button>
                  ))}
                </div>
              </Box>
            </Grid>
          ) : availColumnsWithResourceDetail &&
            availColumnsWithResourceDetail?.data?.length > 0 ? (
            // If `availColumnsWithResourceDetail` exists and has data
            availColumnsWithResourceDetail?.queryType === "ADHOCQUERY" ? (
              // If `queryType` is "ADHOCQUERY"
              availColumnsWithResourceDetail?.data?.map((detail, index) => (
                <Grid xs={12} item md={isQueryColumns ? 12 : 6} key={index}>
                  <Box
                    sx={{ marginBottom: 3 }}
                    className="available-box box-alt avcrd"
                  >
                    <h3 className="mb-0 mb-8">
                      Available Columns for Resource: {detail?.resourceName}
                    </h3>
                    <div className="avail-columns-group">
                      {detail?.rcolums?.map((column: any, idx: any) => (
                        <button
                          className="avail-columns"
                          key={idx}
                          onClick={() => handleColumnSelect(column)}
                        >
                          {column}
                        </button>
                      ))}
                    </div>
                  </Box>
                </Grid>
              ))
            ) : (
              // If `queryType` is not "ADHOCQUERY"
              <Grid item xs={12} md={isQueryColumns ? 12 : 6}>
                <Box
                  sx={{ marginBottom: 3 }}
                  className="available-box box-alt avc"
                >

                  <h3 className="mb-0 mb-8">Available Columns:</h3>

                  <div className="avail-columns-group">
                    {availColumnsWithResourceDetail?.data?.map(
                      (column, index) => (
                        <button
                          className="avail-columns"
                          key={index}
                          onClick={() => handleColumnSelect(column)}
                        >
                          {column}
                        </button>
                      ),
                    )}
                  </div>
                </Box>
              </Grid>
            )
          ) : availColumns &&
            (Array.isArray(availColumns) ? availColumns.length > 0 : true) ? (
            // Use new AvailableColumnsTabs component for structured or simple array data
            <AvailableColumnsTabs
              availColumns={availColumns}
              handleColumnSelect={handleColumnSelect}
              isQueryColumns={isQueryColumns}
            />
          ) : // If none of the above conditions are true
          null)}
        {/* Conditionally render ResourceColumnsChips for backward compatibility */}
        {showResourceColumnsChips && mergeQuerycolumns ? (
          <Grid item xs={12} md={isQueryColumns ? 12 : 6}>
            <ResourceColumnsChips
              setEditorValue={setQueryBuilderTempValue}
              editorRef={editorRef}
              columns={mergeQuerycolumns}
              isFinalQuery={isFinalQuery}
              dialogType={dialogType ?? ""}
              queryBuilderType={queryBuilderType ?? ""}
              mergeQueryType={mergeQueryType}
            />
          </Grid>
        ) : (
          !showResourceColumnsChips &&
          !hideAvailableColumns &&
          showAggregatedColumns === false &&
          (() => {
            // Check if availColumns is empty (handle both array and structured data)
            const isEmpty = Array.isArray(availColumns)
              ? availColumns.length === 0
              : !(availColumns as any)?.flatColumns?.length;
            return isEmpty;
          })() && <p className="no-columns">No columns available.</p>
        )}
      </Grid>
    </>
  );
};

export default SQLEditor;
